import { FC, SVGProps } from "react";

import { BodyTypes } from "@/constants/bodyType";
import { QuestionTypes } from "@/constants/questionsIds";

type IconComponent = FC<SVGProps<SVGSVGElement> & { title?: string }>;

type CommonAnswer = { id: string; text: string };
type AnswerWithDescription = CommonAnswer & { description?: string };
type AnswerWithImg = CommonAnswer & { img?: string };

export interface AnswerVariantTypeByQuestionType {
    [QuestionTypes.Checkbox]: AnswerWithImg &
        AnswerWithDescription & {
            svg?: IconComponent;
            isPopular?: boolean;
        };
    [QuestionTypes.CheckboxImageLeft]: AnswerWithImg;
    [QuestionTypes.CheckboxInline]: AnswerWithImg & { selectedImg?: string };
    [QuestionTypes.Radio]: AnswerWithDescription & {
        svg?: IconComponent;
        noticeMessage?: string;
    };
    [QuestionTypes.RadioMotivation]: AnswerWithDescription & {
        isGradientRow: boolean;
    };
    [QuestionTypes.RadioImageLeft]: AnswerWithImg &
        AnswerWithDescription & {
            svg?: IconComponent;
            noticeMessage?: string;
        };
    [QuestionTypes.Range]: Omit<AnswerWithImg, "text"> & { value: string };
    [QuestionTypes.RadioCards]: AnswerWithImg;
    [QuestionTypes.YesNoWithImage]: CommonAnswer;
    [QuestionTypes.YesNo]: CommonAnswer & {
        icon?: IconComponent;
    };
    [QuestionTypes.MotivationalScreen]: never;
}
export const enum TargetAreasAnswers {
    Belly = "abs",
    FullBody = "full_body",
    Butt = "glutes",
    Chest = "chest",
    Legs = "legs",
    Shoulders = "shoulders",
    Arms = "arms",
    Back = "core",
}

export const enum EventAnswers {
    Vacation = "vacation",
    Wedding = "wedding",
    Reunion = "reunion",
    SchoolReunion = "school_reunion",
    Birthday = "birthday",
    FamilyGathering = "family_gathering",
    Holiday = "holiday",
    SportingEvent = "sporting_event",
    Other = "other",
    None = "none",
}

export const enum ActionPlanAnswers {
    AsQuicklyAsPossible = "as_quickly_as_possible",
    SlowAndSteady = "slow_and_steady",
    SomewhereBetweenTheTwo = "somewhere_between_the_two",
}

export const enum PaceAnswers {
    Fast = "fast",
    Steady = "steady",
    BetweenTwo = "between_two",
}

export const enum WeightLossGoal {
    OneToTen = "1-10",
    ElevenToTwenty = "11-20",
    TwentyPlus = "20+",
    MaintainWeight = "maintain_weight",
    NotDecided = "not_decided",
}

export const enum HealthProblemsAnswers {
    BackPain = "backPain",
    KneePain = "kneePain",
    ShoulderPain = "shoulderPain",
    NeckPain = "neckPain",
    ArmPain = "armPain",
    ElbowPain = "elbowPain",
    LegPain = "legPain",
    FootPain = "footPain",
    None = "none",
}

export const enum SportPreferAnswers {
    Morning = "morning",
    Afternoon = "afternoon",
    Evening = "evening",
    Different = "different",
}

export const enum DesiredWorkoutTimeAnswers {
    Thirty = "30",
    FortyFive = "45",
    Sixty = "60",
    SixtyPlus = "60+",
}

export const enum DailyWalkAnswers {
    Few = "few",
    Average = "average",
    Lot = "lot",
}

export const enum OtherSportsAnswers {
    No = "no",
    Cardio = "cardio",
    Flexibility = "flexibility",
    MartialArts = "martial_arts",
    GameSports = "game_sports",
}

export const enum Train3MonthsAnswers {
    HardlyEver = "hardly_ever",
    One2PerWeek = "1_2_per_week",
    Three4PerWeek = "3_4_per_week",
    Five6PerWeek = "5_6_per_week",
    Everyday = "everyday",
}

export const enum CommitmentReadinessAnswers {
    FirstWorkoutTomorrow = "first_workout_tomorrow",
    FirstWorkoutToday = "first_workout_today",
    NotReady = "not_ready",
}

export const enum WorkoutFrequencyAnswers {
    NoWorkout = "no_workout",
    One2PerWeek = "1_2_times_per_week",
    Three4PerWeek = "3_4_times_per_week",
    AlmostDaily = "almost_daily",
}

export const enum PushUpsSquartsAnswers {
    Few = "few",
    Average = "average",
    Lot = "lot",
    NoInfo = "no_info",
}

export const enum WaterConsumption {
    NoWater = "no_water",
    Low = "low",
    Average = "average",
    AboveAverage = "above_average",
    Lot = "lot",
}

export const enum ExcitedResults {
    MuscleSafeWeightLoss = "muscle_safe_weight_loss",
    GetStronger = "get_stronger",
    MaintainMyResult = "maintain_my_results_after_glp_1",
    FirmUpAndRegainTone = "firm_up_and_regain_tone",
}

export const enum DesiredBodyAnswers {
    Lean = BodyTypes.Lean,
    Muscular = BodyTypes.Muscular,
    Ripped = BodyTypes.Ripped,
}

export const enum BiggestDriveAnswers {
    Appearance = "appearance",
    EnjoyingTime = "enjoying_time",
    GettingHealthier = "getting_healthier",
    FeelingHappier = "feeling_happier",
    Other = "other",
}

export const enum ObstaclesAnswers {
    ConsistencyLack = "consistencyLack",
    Plateaus = "plateaus",
    ExtraExpectations = "extraExpectations",
    KnowledgeLack = "knowledgeLack",
    AccountabilityLack = "accountabilityLack",
    Stress = "stress",
    HealthIssues = "healthIssues",
    TimeConstraints = "timeConstraints",
    LackOfMotivation = "lackOfMotivation",
    Fatigue = "fatigue",
    LackOfVisibleProgress = "lackOfVisibleProgress",
    PlateausInProgress = "plateausInProgress",
    LackOfSupport = "lackOfSupport",
    InjuriesOrPain = "injuriesOrPain",
    None = "none",
    DontKnow = "dontKnow",
}

export const enum BreathLoseAnswers {
    CantCatchMyBreath = "cant_catch_my_breath",
    OutOfBreath = "out_of_breath",
    FeelGoodAfterClimbing = "feel_good_after_climbing",
    CanHandleSeveralFlights = "can_handle_several_flights",
}

export const enum FeelBetweenMealsAnswers {
    Sleepy = "sleepy",
    Tired = "tired",
    Energized = "energized",
}

export const enum SleepAmountAnswers {
    LessThan5 = "less_than_5",
    From5To6 = "5_6",
    From7To8 = "7_8",
    Over8 = "over_8",
}
export const enum MotivationAnswers {
    ImprovingHealth = "improving_health",
    Immunity = "immunity",
    LookingBetter = "looking_better",
    Strength = "strength",
    LosingPostpartumWeight = "losing_postpartum_weight",
}

export const enum ManMotivationAnswers {
    Necessary = "necessary",
    Inconvenient = "inconvenient",
    Enjoyable = "enjoyable",
}

export const enum DietTypeAnswers {
    NoDiet = "no_diet",
    Diabetes = "diabetes",
    Vegan = "vegan",
    GlutenFree = "gluten_free",
    LactoseFree = "lactose_free",
    NutAllergy = "nut_allergy",
}

export const enum YesNoAnswers {
    Yes = "yes",
    No = "no",
}

export const enum SuccessMeasureAnswers {
    Muscle = "muscle",
    Strength = "strength",
    Endurance = "endurance",
    WeightLoss = "weight_loss",
    Measurements = "measurements",
    Improvements = "improvements",
}

export const enum TrackStrengthAnswers {
    Weights = "weights",
    Milestones = "milestones",
    Reps = "reps",
    CompoundExercises = "compound_exercises",
    Other = "other",
}

export const enum HopeToFeel {
    Energized = "energized",
    Confident = "confident",
    Relaxed = "relaxed",
    Accomplished = "accomplished",
}

export const enum ImportantElementsAnswers {
    Consistency = "consistency",
    Commitment = "commitment",
    Guidance = "guidance",
    ExerciseVariety = "exercise_variety",
    LifestyleChanges = "lifestyle_changes",
    GoalFocus = "goal_focus",
    Convenience = "convenience",
}

export const enum FitnessExperienceAnswers {
    PersonalTrainers = "personal_trainers",
    FitnessApps = "fitness_apps",
    WorkoutVideos = "workout_videos",
    SelfTaught = "self_taught",
    None = "none",
}

export const enum ActivityEngagedAnswers {
    NutritionChanges = "nutrition_changes",
    WeightTraining = "weight_training",
    Cardio = "cardio",
    BodyweightTraining = "bodyweight_training",
    GroupSports = "group_sports",
    MartialArts = "martial_arts",
    Other = "other",
}

export const enum EatingHabitsAnswers {
    Emotional = "emotional",
    Bored = "bored",
    Unconscious = "unconscious",
    Habitual = "habitual",
    Energy = "energy",
}

export const enum CopeStressAnswers {
    Exercising = "exercising",
    BeingCreative = "being_creative",
    BadHabits = "bad_habits",
    Eating = "eating",
    Outdoors = "outdoors",
}

export const enum TrainingTriggerAnswers {
    JobRequirements = "job_requirements",
    ObservingInactivity = "observing_inactivity",
    VisualIndicators = "visual_indicators",
    ParentalStamina = "parental_stamina",
    ClothingFit = "clothing_fit",
    HealthImprovement = "health_improvement",
    AgingConcerns = "aging_concerns",
    CurrentFitnessLevels = "current_fitness_levels",
    SexualPerformance = "sexual_performance",
}

export const enum PreventExerciseConsistentlyAnswers {
    WorkSchedule = "work_schedule",
    Procrastination = "procrastination",
    UnexpectedEvents = "unexpected_events",
    LackOfAccountability = "lack_of_accountability",
}

export const enum AbilityToStayMotivatedAnswers {
    Steadfast = "steadfast",
    Resilient = "resilient",
    Challenging = "challenging",
    Fickle = "fickle",
}

export const enum MotivateMostAnswers {
    ProgressTracking = "progress_tracking",
    FeelingBetter = "feeling_better",
    Achievements = "achievements",
    Discipline = "discipline",
    Music = "music",
    Challenges = "challenges",
}

export const enum AchieveFitnessGoalAnswers {
    HighlyConfident = "highly_confident",
    ReasonablyConfident = "reasonably_confident",
    Optimistic = "optimistic",
    NotConfident = "not_confident",
}

export const enum UnhappyAboutCurrentFitnessAnswers {
    ClothingFit = "clothing_fit",
    LackOfEnergy = "lack_of_energy",
    NotSeeingProgress = "not_seeing_progress",
    InconsistentRoutine = "inconsistent_routine",
    WeightGainDespiteActivity = "weight_gain_despite_activity",
    PainAndDiscomfort = "pain_and_discomfort",
}

export const enum RightTrackAnswers {
    ClothesMoreComfortable = "clothes_more_comfortable",
    FullWeekOfWorkouts = "full_week_of_workouts",
    ClimbingStairs = "climbing_stairs",
    BestSleep = "best_sleep",
    FeelFocused = "feel_focused",
    SomeoneNoticedMyChanges = "someone_noticed_my_changes",
    MoreEnergetic = "more_energetic",
}

export const enum TypicalDayAnswers {
    Sitting = "sitting",
    Moving = "moving",
    OnFeet = "on_feet",
}

export const enum WeightTypicalChangeAnswers {
    GainFastLoseSlowly = "gain_fast_lose_slowly",
    GainAndLoseEasily = "gain_and_lose_easily",
    StruggleToGainWeightOrMuscle = "struggle_to_gain_weight_or_muscle",
}

export const enum WalkFrequencyAnswers {
    AlmostEveryDay = "almost_every_day",
    ThreeFourPerWeek = "3_4_times_per_week",
    OneTwoPerWeek = "1_2_times_per_week",
    OnceAMonth = "once_a_month",
}

export const enum EnergyLevelAnswers {
    Low = "low",
    PostLunchSlump = "post_lunch_slump",
    DraggingBeforeMeals = "dragging_before_meals",
    HighAndSteady = "high_and_steady",
}

export const enum SeniorsWorkoutFrequencyAnswers {
    AlmostEveryDay = "almost_every_day",
    SeveralTimesAWeek = "several_times_a_week",
    SeveralTimesAMonth = "several_times_a_month",
    Never = "never",
}

export const enum BestShapeOfYourLifeAnswers {
    CantRemember = "cant_remember",
    OneToTwoYearsAgo = "1_2_years_ago",
    MoreThanThreeYearsAgo = "more_than_3_years_ago",
    Never = "never",
}

export const enum UpgradedBestShapeOfYourLifeAnswers {
    LessThanAYearAgo = "less_than_a_year_ago",
    OneToTwoYearsAgo = "1_2_years_ago",
    MoreThanThreeYearsAgo = "more_than_3_years_ago",
    Never = "never",
}

export enum MalePrimaryGoalAnswersReorderReactionExp {
    FirmerDefinedBody = "firmer_defined_body",
    MoreMuscleStrength = "more_muscle_strength",
    EffectiveWeightLoss = "effective_weight_loss",
    MentalBalanceStressRelief = "mental_balance_stress_relief",
}

export const enum PrimaryGoalAnswersGLP1Exp {
    MuscleSafeWeightLoss = "muscle_safe_weight_loss",
    MaintainMyResultsAfterGlp1 = "maintain_my_results_after_glp_1",
    FirmUpAndRegainTone = "firm_up_and_regain_tone",
    GetStronger = "get_stronger",
}

export const enum PrimaryGoalAnswersMedicationGPL1 {
    Medication_YES = "yes",
    Medication_No_WITH_DESC = "not_yet_but_i_have_a_prescription",
    Medication_NO_WITH_BUT = "no_but_i_am_considering",
    Medication_NO = "no",
}

export enum MalePrimaryGoalAnswersUpgraded {
    FirmerBody = "firmer_body",
    MoreMuscle = "more_muscle",
    WeightLoss = "weight_loss",
    ReduceStress = "reduce_stress",
}

export const enum TrainingAndNutritionAnswers {
    StartingFromScratch = "starting_from_scratch",
    GoodHabitsButInconsistent = "good_habits_but_inconsistent",
    StuckDespiteEffort = "stuck_despite_effort",
}

export const enum TimeForYourselfAnswers {
    MostlyBusy = "mostly_busy",
    TryToFindSomeTime = "try_to_find_some_time",
    OpenAndFlexible = "open_and_flexible",
}

export const enum BadHabitsAnswers {
    Overeating = "overeating",
    BoredomEating = "boredom_eating",
    LateNightSnacking = "late_night_snacking",
    SkippingMealsTooOften = "skipping_meals_too_often",
    None = "none",
}

export const enum HealthProblemsAnswersUpgraded {
    Shoulders = "shoulders",
    Back = "back",
    Knees = "knees",
    SomethingElse = "something_else",
    None = "none",
}

export const enum CoachResponseAnswers {
    John = "john",
    Jennifer = "jennifer",
    Sarah = "sarah",
}

export const enum UpgradedWorkoutFrequencyAnswers {
    OneWorkoutPerWeek = "1_workout_per_week",
    TwoWorkoutPerWeek = "2_workout_per_week",
    ThreeWorkoutPerWeek = "3_workout_per_week",
    FourWorkoutPerWeek = "4_workout_per_week",
    FiveWorkoutPerWeek = "5_workout_per_week",
    SixWorkoutPerWeek = "6_workout_per_week",
    EveryDay = "every_day",
}

export const enum UpgradedWorkoutDurationAnswers {
    Burst = "burst",
    Boost = "boost",
    Typical = "typical",
    Power = "power",
    Challenging = "challenging",
    Advanced = "advanced",
}
